import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  inject,
  signal
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { iif, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { SelectedSuggestion, Suggestion } from '../../model/suggestions';
import { LiveAnnouncer } from '../../services/a11y';
import { CleanupService } from '../../shared/utils/cleanup.service';

const KEY_ENTER = 'Enter';
const KEY_ARROW_UP = 'ArrowUp';
const KEY_ARROW_DOWN = 'ArrowDown';
const KEY_ESC = 'Escape';
const KEY_ALT = 'Alt';

export enum Mode {
  Suggestion, // Nur eine Suggestion ist eine valide Auswahlmöglichkeit (wie z.B. beim Ort)
  Text // Normaler Text ist eine valide Auswahlmöglichkeit (es ist egal, ob es eine Suggestion gibt oder nicht)
}

export type LoadSuggestions = (value: string) => Observable<Suggestion[]>;

/**
 * Liefert das HTML zu einer Suggestion, das im Dropdown-LI innerhalbe des A-Elements verwendet wird.
 */
export type SuggestionRenderer = (
  suggestion: Suggestion,
  searchInput: string,
  highlighter: Highlighter
) => SafeHtml;

/**
 * Möglichkeit Substrings in einem Text hervorzuheben.
 */
export type Highlighter = (text: string, substring: string) => string;

@Component({
  selector: 'now-wbsuche-lib-typeahead',
  templateUrl: './typeahead.component.html',
  styleUrls: ['./typeahead.component.scss'],
  imports: [CommonModule, ReactiveFormsModule]
})
export class TypeaheadComponent implements OnInit {
  private readonly announcer = inject(LiveAnnouncer);
  private readonly elRef = inject(ElementRef);
  private readonly cleanupService = inject(CleanupService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  constructor() {
    this.subscribeValueChanges();
  }

  public inputControl: FormControl<string | null> = new FormControl({
    value: '',
    disabled: false
  });

  public readonly inputContent$ = signal<string>('');

  @Output()
  public suggestionSelected = new EventEmitter<SelectedSuggestion | null>();
  @ViewChildren('option') options?: QueryList<ElementRef>;
  public suggestions: Suggestion[] = [];
  @Input()
  public id!: string;
  @Input()
  public placeholder = '';
  @Input()
  public label!: string;

  @Input()
  public hilfstext?: string;

  @Input()
  public set initialSearchInput(value: string) {
    this.selectedSuggestionText = value;
    this.setText(value);
  }

  @Input()
  public mode = Mode.Suggestion;

  @Input()
  public resetOnSubmit = true;
  public activeSuggestionIndex = -1;
  public showSuggestions = false;
  /**
   * Maximale Zeichenanzahl im Input-Feld.
   */
  @Input()
  public maxlength: string = '';

  public form!: FormGroup<{ typeahead: FormControl<string | null> }>;

  @Input()
  public set disable(disabled: boolean) {
    if (disabled) {
      this.inputControl?.disable();
    } else {
      this.inputControl?.enable();
    }
  }

  @Input()
  public iconClass: any;
  @ViewChild('suggestionsUl', { static: false })
  private readonly suggestionsUl?: ElementRef;
  @ViewChild('inputField', { static: false })
  private readonly inputField?: ElementRef;
  @ViewChild('formGroup')
  private readonly formGroupElement!: ElementRef<HTMLElement>;
  // Vom Benutzer eingegebener Wert; wird gespeichert, damit dieser bei KeyUp wieder hergestellt werden kann
  private originalInput?: string;
  private loadSuggestionsInProgress = false;
  // Hilfs-Flag zur Markierung eines ausstehenden Submits, während eine Autocomplete-Liste gerade geladen wird.
  private submitPending = false;
  // Hilfs-Flag zur Markierung einer Textänderung, die nicht durch eine direkte User-Eingabe verursacht wurde.
  private nonUserTextChange = false;
  private selectedSuggestionText = '';

  /**
   * Lädt Vorschläge zu einem gegebenen Suchwort.
   */
  @Input()
  public loadSuggestions: LoadSuggestions = () => of([]);

  // befindet sich der focus außerhalb der komponente?
  private outside = true;
  // könnte sein, dass wir uns außerhalb der komponente befinden
  private outsideMaybe = false;

  /**
   * Möglichkeit den Default-Renderer zu überschreiben.
   * Dies kann z.B. genutzt werden, um in den Vorschlägen rechts noch die Trefferanzahl anzuzeigen.
   * Der Default-Renderer zeigt einfach die Labels (ge-highlight-et) an.
   */
  @Input()
  public suggestionRenderer: SuggestionRenderer = (suggestion, searchInput, highlighter) => {
    return highlighter(suggestion.label, searchInput);
  };

  public ngOnInit() {
    this.elRef.nativeElement.addEventListener('focusin', () => this.focusin());
    this.elRef.nativeElement.addEventListener('focusout', () => this.focusout());

    this.form = new FormGroup({ typeahead: this.inputControl });

    if (this.initialSearchInput != null) {
      this.inputControl.setValue(this.initialSearchInput);
    }
  }

  private subscribeValueChanges(): void {
    this.inputControl.valueChanges
      .pipe(
        takeUntil(this.cleanupService.destroyed$),
        map((query) => query ?? ''),
        tap((value) => this.inputContent$.set(value)),
        filter(() => !this.nonUserTextChange),
        tap(() => (this.loadSuggestionsInProgress = true)), // Vor debounceTime!
        tap(() => (this.originalInput = this.inputControl.value!)), // Vor debounceTime!
        debounceTime(200),
        switchMap((query: string) => {
          return this.load(query);
        })
      )
      .subscribe((suggestions) => {
        this.loadSuggestionsInProgress = false;
        this.activeSuggestionIndex = -1;
        if (suggestions == null) {
          // null heißt: keine Vorschläge anzeigen
          this.close();
        } else {
          this.suggestions = suggestions;
          if (this.submitPending) {
            this.submitSelection();
          }
          this.showSuggestions = suggestions.length > 0 && !this.outside;
        }
        if (this.showSuggestions) {
          if (this.suggestions.length === 1 && this.suggestions[0].isReadOnly) {
            this.announcer.announce(this.suggestions[0].label);
          } else {
            this.announcer.announce(
              `${suggestions.length} Ergebnisse stehen zur Verfügung. Nutzen Sie die Hoch- und Runter-Pfeiltasten zum Navigieren und die Enter-Taste zum Selektieren.`
            );
          }
        }

        //TODO: Flo fragen, warum das jetzt auf einmal benötigt wird.
        // ohne das hängt die Changedetection immer einen change hinter. Wenn man "Reg" eingibt sieht man die vorschläge für "Re"
        this.changeDetectorRef.detectChanges();
      });
  }

  // von clients aufrufbar
  private setText(text: string) {
    this.nonUserTextChange = true;
    this.inputControl.patchValue(text);
    this.nonUserTextChange = false;
  }

  public onKeyUp(event: KeyboardEvent): void {
    this.submitPending = false;
    switch (event.key) {
      case KEY_ENTER:
        event.preventDefault();
        this.submitPending = true; // vor dem Test auf Oracle pending, damit das Submit keinesfalls verloren geht!
        this.handleTypeaheadAction();
        break;
      case KEY_ARROW_UP:
      case 'Up': // IE
        if (this.showSuggestions) {
          this.handleItemChange(event, -1);
        }
        break;
      case KEY_ARROW_DOWN:
      case 'Down': // IE
        if (this.showSuggestions) {
          this.handleItemChange(event, +1);
        }
        break;
      case KEY_ESC:
      case KEY_ALT: // bei aktiviertem JAWS wird ALT statt ESC erkannt
        this.close();
        this.activeSuggestionIndex = -1;
        this.setText(this.originalInput!);
        break;
    }
  }

  public onClickSuggestion(idx: number) {
    const item = this.suggestions[idx];
    if (!item.isReadOnly) {
      this.activeSuggestionIndex = idx;
      this.setText(item.label);
      this.submitSelection();
      this.close();
    }
  }

  public render(suggestion: Suggestion) {
    if (!suggestion.value && suggestion.isReadOnly) {
      return suggestion.label;
    }
    return this.suggestionRenderer(suggestion, this.originalInput!, this.highlighter);
  }

  public setActive(idx: number) {
    this.activeSuggestionIndex = idx;
  }

  public focusOut(event: FocusEvent) {
    //Wenn nur Suggestions valide sind und dann außerhalb des inputs geklickt wird,
    // dann resete input wert auf die letzten valide Suggestion.
    if (this.mode === Mode.Suggestion) {
      // Check if click was outside the typeahead Component
      if (!this.formGroupElement.nativeElement.contains(event.relatedTarget as Node)) {
        if (!this.inputField!.nativeElement.value && this.selectedSuggestionText !== '') {
          this.suggestionSelected.emit(null);
        } else if (this.inputField!.nativeElement.value != this.selectedSuggestionText) {
          this.suggestions = [];
          this.inputControl.setValue(this.selectedSuggestionText);
        }
      }
    }
  }

  /**
   * Löscht die Eingabe und schließt (erst mal) das Popup, fokussiert das Eingabeelement
   */
  public cleanAndClose() {
    this.close();
    this.suggestions = [];
    this.inputField!.nativeElement.value = '';
    this.inputControl.setValue('');
    this.inputField!.nativeElement.focus();
  }

  private close() {
    this.showSuggestions = false;
  }

  private load(query: string): Observable<Suggestion[]> {
    const enteredValue = query.trim();
    return iif(
      () => !enteredValue,
      of([]),
      this.loadSuggestions(enteredValue).pipe(
        catchError(() => {
          return of([]);
        })
      )
    );
  }

  /**
   * Markiert Vorkommen von Substring in Text fett.
   */
  private readonly highlighter: Highlighter = (word, substring) => {
    let result = word;
    const searchWord = substring.toLowerCase();
    const searchPos = word.toLowerCase().indexOf(searchWord);
    if (searchPos > -1) {
      const searchLen = searchWord.length;
      const prefix = searchPos <= 0 ? '' : word.substring(0, searchPos);
      const postfix =
        searchPos + searchLen === word.length ? '' : word.substring(searchPos + searchLen);
      result = prefix + '<b>' + word.substring(searchPos, searchLen) + '</b>' + postfix;
    }
    return result;
  };

  /**
   * Submits the currently selected value
   */
  private submitSelection(): boolean {
    if (
      !this.inputControl.value ||
      this.inputControl.value.length === 0 ||
      this.suggestions.length === 0 ||
      this.suggestions[0].isReadOnly
    ) {
      this.submitPending = false;
      return false;
    }
    const suchwort = this.inputControl.value;

    let selected: Suggestion;
    if (this.activeSuggestionIndex < 0) {
      // erstes nehmen
      selected = this.suggestions[0];
    } else {
      selected = this.suggestions[this.activeSuggestionIndex];
    }

    this.selectedSuggestionText = selected.label;
    this.suggestionSelected.emit(
      new SelectedSuggestion(
        suchwort,
        selected.value,
        this.activeSuggestionIndex < 0,
        selected.extra
      )
    );
    this.submitPending = false;
    this.close();
    this.originalInput = '';
    if (this.resetOnSubmit) {
      this.suggestions = [];
      this.inputControl.setValue('');
    }
    return true;
  }

  /**
   * Handles change of the currently selected element according to the given index offset.
   *
   * Also prevents the default function for the event in order to not change cursor position within the input field.
   */
  private handleItemChange(event: KeyboardEvent, offset: number): void {
    // Verhindert Cursor-Bewegungen innerhalb des Eingabefeldes.
    event.preventDefault();

    const nextIndex = this.nextIndex(this.activeSuggestionIndex, offset);
    if (this.activeSuggestionIndex !== nextIndex) {
      if (nextIndex === -1) {
        this.activeSuggestionIndex = -1;
        this.setText(this.originalInput!);
        return;
      } else {
        this.activeSuggestionIndex = nextIndex;
        // Dropdown ggf. so scrollen, dass das aktuelle Item sichtbar wird.
        this.scrollToView(this.activeSuggestionIndex);
        this.setText(this.suggestions[this.activeSuggestionIndex].label || '');
      }
    }
  }

  private nextIndex(currentIndex: number, offset: number): number {
    const max = this.suggestions.length - 1;
    let next = currentIndex + offset;
    if (next < -1) {
      next = max;
    } else if (next > max) {
      next = -1;
    }
    if (next === -1) {
      return next;
    }
    if (this.suggestions[next].isReadOnly) {
      return this.nextIndex(next, offset);
    }
    return next;
  }

  /**
   * Scrollt zu dem Eintrag mit dem index in der Autocomplete-Liste index; dieser wird bei keydown/key events berechnet
   * @param index Index des zu scrollenden Elenets in der Autocomplete-Box
   */
  private scrollToView(index: number): void {
    const ul = this.suggestionsUl?.nativeElement;
    const li = ul.querySelector('li'); // just sample the first li to get height
    const liHeight = li.offsetHeight;
    const scrollTop = ul.scrollTop;
    const viewport = scrollTop + ul.offsetHeight;
    const scrollOffset = liHeight * index;
    if (scrollOffset < scrollTop || scrollOffset + liHeight > viewport) {
      ul.scrollTop = scrollOffset;
    }
  }

  private focusin() {
    if (this.outside) {
      this.showSuggestions = this.suggestions && this.suggestions.length > 0;
      this.outside = false;
    }
    this.outsideMaybe = false;
  }

  private focusout() {
    this.outsideMaybe = true;
    setTimeout(() => {
      if (this.outsideMaybe) {
        this.close();
        this.outside = true;
        this.changeDetectorRef.detectChanges();
      }
    }, 100);
  }

  private handleTypeaheadAction() {
    if (this.mode === Mode.Suggestion) {
      if (!this.loadSuggestionsInProgress) {
        if (this.activeSuggestionIndex === -1 && this.inputControl.value === '') {
          this.suggestionSelected.emit(null);
        } else if (
          this.activeSuggestionIndex > -1 ||
          this.inputControl.value === this.originalInput
        ) {
          this.submitSelection();
        }
      }
    } else if (this.mode === Mode.Text) {
      const inputValue = this.inputControl.value ?? '';
      this.suggestionSelected.emit(new SelectedSuggestion(inputValue, inputValue, false));
      this.close();
    }
  }
}
